import { useState } from 'react';

import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';
import type { Asset } from '../types';
import { getAssetDisplayName, getFileIcon } from '../utils';

type ThumbnailSize = 'sm' | 'md' | 'lg';

interface AssetThumbnailProps {
  asset: Asset;
  size?: ThumbnailSize;
  className?: string;
}

const sizeClasses: Record<ThumbnailSize, { container: string; icon: string }> = {
  sm: { container: 'size-12', icon: 'size-6' },
  md: { container: 'aspect-square w-full', icon: 'size-16' },
  lg: { container: 'h-[180px] w-full', icon: 'size-24' },
};

export function AssetThumbnail({ asset, size = 'md', className }: AssetThumbnailProps) {
  const [imageError, setImageError] = useState(false);

  // content_type comes from the blob for S3 assets; fall back to metadata when not yet attached
  const contentType = asset.contentType ?? asset.metadata?.contentType;
  const isImage = contentType?.startsWith('image/') ?? false;
  const thumbnailUrl = asset.variants?.small ?? asset.url;
  const sizeConfig = sizeClasses[size];

  if (isImage && !imageError && thumbnailUrl) {
    return (
      <div
        className={cn(
          'overflow-hidden rounded border border-border bg-muted',
          sizeConfig.container,
          className,
        )}
      >
        <img
          src={thumbnailUrl}
          alt={asset.altText || getAssetDisplayName(asset)}
          className="size-full object-contain"
          loading="lazy"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden rounded',
        sizeConfig.container,
        className,
      )}
    >
      <Icon
        icon={getFileIcon(contentType)}
        size="lg"
        className={cn('text-muted-foreground', sizeConfig.icon)}
      />
    </div>
  );
}
